import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { Virtuoso } from 'react-virtuoso';
import './App.css';
import { useTheme } from './useTheme';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3003';

// Configurar axios
const token = localStorage.getItem('token');
if (token) {
  axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  axios.defaults.baseURL = API_BASE_URL;
}

const loadFontAwesome = () => {
  if (!document.getElementById('font-awesome-css')) {
    const link = document.createElement('link');
    link.id = 'font-awesome-css';
    link.rel = 'stylesheet';
    link.href = 'https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css';
    document.head.appendChild(link);
  }
};

const formatDateSeparator = (dateString) => {
  const messageDate = new Date(dateString);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);

  if (messageDate.toDateString() === today.toDateString()) return 'HOY';
  if (messageDate.toDateString() === yesterday.toDateString()) return 'AYER';
  return messageDate.toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' });
};

const getMediaUrl = (path) => {
  if (path.startsWith('http://') || path.startsWith('https://')) return path;
  const isDevelopment = window.location.port === '3000';
  if (isDevelopment) return `http://localhost:3003${path}`;
  return path;
};

function ChatPage() {
  const { contacto } = useParams();
  const navigate = useNavigate();
  const numeroDestino = contacto ? decodeURIComponent(contacto) : null;
  useTheme();
  const [mensajes, setMensajes] = useState([]);
  const [nuevoMensaje, setNuevoMensaje] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [currentResultIndex, setCurrentResultIndex] = useState(0);
  const [showSearchBar, setShowSearchBar] = useState(false);
  const [whatsappConnected, setWhatsappConnected] = useState(true);
  const virtuosoRef = useRef(null);

  useEffect(() => {
    loadFontAwesome();
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('user');
    window.location.href = '/';
  };

  // Search logic
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setSearchResults([]);
      setCurrentResultIndex(0);
      return;
    }
    const term = searchTerm.toLowerCase();
    const results = mensajes
      .map((mensaje, index) => ({ mensaje, originalIndex: index }))
      .filter(({ mensaje }) => {
        if (!mensaje.mensaje || typeof mensaje.mensaje !== 'string') return false;
        if (mensaje.mensaje.startsWith('/media/')) return false;
        return mensaje.mensaje.toLowerCase().includes(term);
      });
    setSearchResults(results);
    setCurrentResultIndex(results.length > 0 ? 0 : -1);
    if (results.length > 0 && virtuosoRef.current) {
      virtuosoRef.current.scrollToIndex({ index: results[0].originalIndex, align: 'center', behavior: 'smooth' });
    }
  }, [searchTerm, mensajes]);

  const handleNextResult = () => {
    if (searchResults.length === 0) return;
    const newIndex = (currentResultIndex + 1) % searchResults.length;
    setCurrentResultIndex(newIndex);
    if (virtuosoRef.current) {
      virtuosoRef.current.scrollToIndex({ index: searchResults[newIndex].originalIndex, align: 'center', behavior: 'smooth' });
    }
  };

  const handlePrevResult = () => {
    if (searchResults.length === 0) return;
    const newIndex = currentResultIndex === 0 ? searchResults.length - 1 : currentResultIndex - 1;
    setCurrentResultIndex(newIndex);
    if (virtuosoRef.current) {
      virtuosoRef.current.scrollToIndex({ index: searchResults[newIndex].originalIndex, align: 'center', behavior: 'smooth' });
    }
  };

  const highlightText = (text, highlight) => {
    if (!highlight.trim()) return text;
    const parts = text.split(new RegExp(`(${highlight})`, 'gi'));
    return parts.map((part, index) =>
      part.toLowerCase() === highlight.toLowerCase()
        ? <mark key={index} style={{ backgroundColor: '#ffd43b', padding: '0 2px', borderRadius: '2px' }}>{part}</mark>
        : part
    );
  };

  // Data fetching
  const fetchMensajes = useCallback(async () => {
    if (!numeroDestino) return;
    try {
      const url = `/api/mensajes?contacto=${encodeURIComponent(numeroDestino)}`;
      const response = await axios.get(url);
      setMensajes(response.data.reverse());
    } catch (error) {
      console.error('Error al obtener los mensajes:', error);
    }
  }, [numeroDestino]);

  useEffect(() => {
    fetchMensajes();
    const isDevelopment = window.location.port === '3000';
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsHost = isDevelopment ? 'localhost:3003' : window.location.host;
    const ws = new WebSocket(`${wsProtocol}//${wsHost}`);

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'qr' || data.type === 'whatsapp-disconnected') {
          setWhatsappConnected(false);
        } else if (data.type === 'whatsapp-ready') {
          setWhatsappConnected(true);
        } else {
          setMensajes(prev => [...prev, data]);
        }
      } catch (error) {
        console.error("Error al procesar mensaje de WebSocket:", error);
      }
    };
    return () => ws.close();
  }, [fetchMensajes]);

  // File handlers
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 16 * 1024 * 1024) {
      alert('El archivo es demasiado grande. Maximo 16MB.');
      return;
    }
    setSelectedFile(file);
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onloadend = () => setFilePreview(reader.result);
      reader.readAsDataURL(file);
    } else {
      setFilePreview(null);
    }
  };

  const handleCancelFile = () => {
    setSelectedFile(null);
    setFilePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSendFile = async () => {
    if (!selectedFile) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('archivo', selectedFile);
    formData.append('numero', numeroDestino);
    formData.append('caption', nuevoMensaje || '');
    try {
      await axios.post('/api/enviar-archivo', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setSelectedFile(null);
      setFilePreview(null);
      setNuevoMensaje('');
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (error) {
      console.error('Error al enviar el archivo:', error);
      alert('Hubo un error al enviar el archivo.');
    } finally {
      setUploading(false);
    }
  };

  const handleEnviarMensaje = async (e) => {
    e.preventDefault();
    if (selectedFile) { await handleSendFile(); return; }
    if (!nuevoMensaje.trim()) return;
    try {
      await axios.post('/api/enviar-mensaje', { numero: numeroDestino, mensaje: nuevoMensaje });
      setNuevoMensaje('');
    } catch (error) {
      console.error('Error al enviar el mensaje:', error);
      alert('Hubo un error al enviar el mensaje.');
    }
  };

  const formatPhoneNumber = (numero) => {
    if (!numero) return '';
    const cleanNumber = numero.replace('@c.us', '');
    if (cleanNumber.length >= 10) {
      const codigo = cleanNumber.slice(0, 2);
      const uno = cleanNumber.slice(2, 3);
      const area = cleanNumber.slice(3, 5);
      const num1 = cleanNumber.slice(5, 9);
      const num2 = cleanNumber.slice(9);
      return `+${codigo} ${uno} ${area} ${num1} ${num2}`;
    }
    return cleanNumber;
  };

  const renderRow = useCallback((index, mensaje) => {
    const prevMensaje = index > 0 ? mensajes[index - 1] : null;
    const showDateSeparator = !prevMensaje || new Date(mensaje.timestamp).toDateString() !== new Date(prevMensaje.timestamp).toDateString();
    const isCurrentSearchResult = searchResults.length > 0 && searchResults[currentResultIndex]?.originalIndex === index;

    return (
      <div className="chat-row-container" style={{ paddingTop: index === 0 ? '0.5rem' : 0 }}>
        {showDateSeparator && (
          <div className="chat-date-separator">
            <span>{formatDateSeparator(mensaje.timestamp)}</span>
          </div>
        )}
        <div className={`chat-item ${mensaje.remitente === 'yo' ? 'sent' : 'received'} ${isCurrentSearchResult ? 'search-highlight' : ''}`}>
          <div className="chat-bubble">
            <div className="chat-bubble-text">
              {mensaje.mensaje && typeof mensaje.mensaje === 'string' && mensaje.mensaje.startsWith('/media/') ? (
                (() => {
                  const path = mensaje.mensaje;
                  const filename = path.split('/').pop();
                  if (path.match(/\.(jpeg|jpg|gif|png|webp)$/i)) {
                    return <img src={getMediaUrl(path)} alt={filename} className="chat-image-preview" />;
                  }
                  if (path.match(/\.(ogg|oga|mp3|wav)$/i)) {
                    return (
                      <div className="chat-audio-player">
                        <audio src={getMediaUrl(path)} controls controlsList="nodownload" />
                      </div>
                    );
                  }
                  return (
                    <div className="chat-file-preview">
                      <i className="fas fa-file" style={{ fontSize: '24px', color: 'var(--text-secondary)' }}></i>
                      <div className="file-info">
                        <span>{filename}</span>
                        <a href={getMediaUrl(path)} target="_blank" rel="noopener noreferrer">Descargar</a>
                      </div>
                    </div>
                  );
                })()
              ) : (
                searchTerm ? highlightText(mensaje.mensaje, searchTerm) : mensaje.mensaje
              )}
            </div>
            <div className="chat-bubble-meta">
              {new Date(mensaje.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </div>
          </div>
        </div>
      </div>
    );
  }, [mensajes, searchTerm, searchResults, currentResultIndex]);

  if (!numeroDestino) {
    return (
      <div className="chat-container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center', color: 'var(--text-secondary)' }}>
          <p>No se ha seleccionado ningun contacto</p>
          <button className="go-conversations-btn" onClick={() => navigate('/conversations')} style={{ display: 'inline-flex' }}>
            Ver conversaciones
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="chat-container">
      {/* Header */}
      <div className="chat-header">
        <button className="chat-header-btn" onClick={() => navigate('/conversations')}>
          <i className="fas fa-arrow-left"></i>
        </button>
        <div className="chat-header-info">
          <span className="chat-header-name">{formatPhoneNumber(numeroDestino)}</span>
        </div>
        <div className="chat-header-actions">
          {showSearchBar ? (
            <div className="chat-search-bar">
              <input
                type="text"
                className="chat-search-input"
                placeholder="Buscar..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                autoFocus
              />
              {searchResults.length > 0 && (
                <span className="chat-search-count">
                  {currentResultIndex + 1}/{searchResults.length}
                </span>
              )}
              {searchResults.length > 0 && (
                <>
                  <button className="chat-header-btn" onClick={handlePrevResult}><i className="fas fa-chevron-up"></i></button>
                  <button className="chat-header-btn" onClick={handleNextResult}><i className="fas fa-chevron-down"></i></button>
                </>
              )}
              <button className="chat-header-btn" onClick={() => { setShowSearchBar(false); setSearchTerm(''); }}>
                <i className="fas fa-times"></i>
              </button>
            </div>
          ) : (
            <>
              <button className="chat-header-btn" onClick={() => setShowSearchBar(true)} title="Buscar">
                <i className="fas fa-search"></i>
              </button>
              <button className="chat-header-btn" onClick={handleLogout} title="Cerrar sesion">
                <i className="fas fa-sign-out-alt"></i>
              </button>
            </>
          )}
        </div>
      </div>

      {/* WhatsApp disconnected banner */}
      {!whatsappConnected && (
        <div className="chat-disconnected-banner" onClick={() => navigate('/status')}>
          <i className="fas fa-exclamation-triangle"></i>
          <span>WhatsApp desconectado - Toca para reconectar</span>
        </div>
      )}

      {/* Messages */}
      <Virtuoso
        ref={virtuosoRef}
        className="chat-messages"
        data={mensajes}
        followOutput="auto"
        initialTopMostItemIndex={mensajes.length - 1}
        itemContent={renderRow}
      />

      {/* Footer */}
      <div className="chat-footer">
        {selectedFile && (
          <div className="chat-file-preview-bar">
            <div className="chat-file-preview-info">
              {filePreview ? (
                <img src={filePreview} alt="Preview" className="chat-file-thumb" />
              ) : (
                <i className="fas fa-file" style={{ fontSize: '24px', color: 'var(--text-secondary)' }}></i>
              )}
              <div>
                <div style={{ fontWeight: 500, color: 'var(--text-primary)', fontSize: '14px' }}>{selectedFile.name}</div>
                <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{(selectedFile.size / 1024).toFixed(0)} KB</div>
              </div>
            </div>
            <button className="chat-header-btn" onClick={handleCancelFile}>
              <i className="fas fa-times"></i>
            </button>
          </div>
        )}
        <form onSubmit={handleEnviarMensaje} className="chat-input-row">
          <input
            ref={fileInputRef}
            type="file"
            style={{ display: 'none' }}
            onChange={handleFileSelect}
            accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
          />
          <button type="button" className="chat-input-btn" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
            <i className="fas fa-paperclip"></i>
          </button>
          <input
            type="text"
            className="chat-input"
            placeholder={selectedFile ? "Comentario (opcional)..." : "Mensaje..."}
            value={nuevoMensaje}
            onChange={(e) => setNuevoMensaje(e.target.value)}
            disabled={uploading}
          />
          <button type="submit" className="chat-send-btn" disabled={uploading || (!nuevoMensaje.trim() && !selectedFile)}>
            {uploading ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-paper-plane"></i>}
          </button>
        </form>
      </div>
    </div>
  );
}

export default ChatPage;
